import React, { useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useQuery } from "react-query";
import { getClientBankOperations } from "@/service/weexpertService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";


// Helper function to get badge props for operation type
function getTypeBadgeProps(type) {
  const typeLower = type?.toLowerCase() || '';
  
  if (typeLower.includes('withdraw') || typeLower.includes('deposit') || typeLower.includes('income')) {
    return {
      variant: "default",
      className: "bg-indigo-700 text-white border-transparent hover:bg-indigo-700  "
    };
  }
  
  if (typeLower.includes('tax_prepaid') || typeLower.includes('withdrawal') || typeLower.includes('expense')) {
    return {
      variant: "outline",
      className: "bg-sky-600 text-white border-sky-600"
    };
  }
  
  if (typeLower == ('fee') || typeLower.includes('exchange')) {
    return {
      variant: "secondary",
      className: "bg-orange-400 text-white border-bg-orange-400 hover:bg-orange-400"
    };
  }
  
  // Default for unknown types
  return {
    variant: "outline",
    className: "bg-amber-300 text-white border-amber-300"
  };
}

// Helper function to get badge props for operation status
function getStatusBadgeProps(status) {
  const statusLower = status?.toLowerCase() || '';
  
  if (statusLower.includes('completed') || statusLower.includes('success') || statusLower.includes('active') || statusLower.includes('approved')) {
    return {
      variant: "default",
      className: "bg-green-500 text-white border border-green-500 hover:bg-green-500"
    };
  }
  
  if (statusLower.includes('pending') || statusLower.includes('processing') || statusLower.includes('waiting')) {
    return {
      variant: "outline",
      className: "bg-indigo-500 text-white border-pirpleLightBg h-6"
    };
  }
  
  if (statusLower.includes('failed') || statusLower.includes('error') || statusLower.includes('cancelled') || statusLower.includes('rejected')) {
    return {
      variant: "destructive"
    };
  }
  
  if (statusLower.includes('inactive') || statusLower.includes('disabled')) {
    return {
      variant: "outline",
      className: "bg-grayBg text-blackText"
    };
  }
  
  // Default for unknown statuses
  return {
    variant: "secondary"
  };
}

// Separate component for balance table to properly use hooks
function BalanceTable({ balance, t }) {
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        cell: ({ row }) => (
          <Link
            to={`/operation/${row.original.id}`}
            className="block w-full"
          >
            {row.original.id}
          </Link>
        ),
      },
      {
        accessorKey: "type",
        header: t("type"),
        cell: ({ row }) => {
          console.log('---------> '+ JSON.stringify(row,null,2))
          const typeBadgeProps = getTypeBadgeProps(row.original.type);
          return (
            <Badge {...typeBadgeProps} >
              {row.original.type}
            </Badge>
          );
        },
      },
      {
        accessorKey: "status",
        header: t("status"),
        cell: ({ row }) => {
          const statusBadgeProps = getStatusBadgeProps(row.original.status);
          return (
            <Badge {...statusBadgeProps}>
              {row.original.status}
            </Badge>
          );
        },
      },
      {
        accessorKey: "currency",
        header: t("currency"),
        cell: ({ row }) => row.original.currency,
      },
      {
        accessorKey: "amount",
        header: t("amount"),
        cell: ({ row }) => row.original.amount,
      },
      {
        accessorKey: "comment",
        header: t("comment"),
        cell: ({ row }) => row.original.comment,
      },
      {
        accessorKey: "createdAt",
        header: t("created_at"),
        cell: ({ row }) =>
          format(new Date(row.original.createdAt), "dd/MM/yyyy"),
      },
      {
        accessorKey: "updatedAt",
        header: t("updated_At"),
        cell: ({ row }) =>
          format(new Date(row.original.updatedAt), "dd/MM/yyyy"),
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: balance.operations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TabsContent value={balance.currency}>
      <div className="overflow-x-auto bg-white rounded-lg shadow ">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="pt-3 pb-3">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="border-b">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-4 py-2">
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TabsContent>
  );
}

export default function ClientBalanceTabs() {
  const { t } = useTranslation();

  // Fetch client bank operations data
  const { data: bankOperationsData, isLoading, error } = useQuery({
    queryKey: ['clientBankOperations'],
    queryFn: getClientBankOperations,
    retry: false,
    refetchOnWindowFocus: false
  });

  const balances = bankOperationsData?.data?.data?.balances || [];

  if (isLoading) {
    return (
      <div className="w-full p-8">
        <div className="ml-0 md:ml-8 text-2xl md:text-4xl mt-4 pl-8 md:pl-0">
          {t("navigation:bank_operations")}
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-8">
        <div className="ml-0 md:ml-8 text-2xl md:text-4xl mt-4 pl-8 md:pl-0">
          {t("navigation:bank_operations")}
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-500">
            Error loading bank operations: {error?.response?.data?.message || error.message}
          </div>
        </div>
      </div>
    );
  }

  // Check if there are any operations across all balances
  const hasOperations = balances.some(balance => balance.operations && balance.operations.length > 0);
  
  if (!balances.length || !hasOperations) {
    return (
      <div className="w-full p-8">
        <div className="ml-0 md:ml-8 text-2xl md:text-4xl mt-4 pl-8 md:pl-0">
          {t("navigation:bank_operations")}
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-500">
            No bank operations found
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ml-0 md:ml-8 text-2xl md:text-4xl mt-4 pl-8 md:pl-0">
        {t("navigation:bank_operations")}
      </div>

      <Tabs defaultValue={balances[0]?.currency} className="w-full p-6">
        <TabsList className="mb-4">
          {balances.map((balance) => (
            <TabsTrigger
              key={balance.id}
              value={balance.currency}
              className="min-w-[120px] text-center"
            >
              {balance.currency}
              <div className="ml-2 text-xs">
                {balance.availableAmount}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {balances.map((balance) => (
          <BalanceTable key={balance.id} balance={balance} t={t} />
        ))}
      </Tabs>
    </>
  );
}
